import { useCallback, useEffect, useState, useTransition } from "react";
import { NamedEntity } from "~/types/NamedEntity.ts";
import { PackItem } from "~/types/PackItem.ts";
import { homeCopy } from "./copy.ts";
import { WITHOUT_MEMBERS_ID } from "./filterUtils.ts";

export type StatusFilter = "all" | "packed" | "unpacked";

export type FilterDialogState = {
  visible: boolean;
  categories: NamedEntity[];
  selectedCategories: string[];
  members: NamedEntity[];
  selectedMembers: string[];
  statusFilter: StatusFilter;
  hasActiveFilter: boolean;
  open: () => void;
  close: () => void;
  onToggleCategory: (categoryId: string) => void;
  onToggleMember: (memberId: string) => void;
  onSetStatus: (status: StatusFilter) => void;
  onClear: () => void;
};

const CATEGORY_KEY = "filteredCategories";
const MEMBER_KEY = "filteredMembers";
const STATUS_KEY = "filteredStatus";

const getStorageKey = (baseKey: string, listId: string) => `${baseKey}:${listId}`;

const parseStringArray = (raw: string | null) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
};

const parseStatus = (raw: string | null): StatusFilter => {
  if (raw === "packed" || raw === "unpacked") return raw;
  return "all";
};

const persistCategories = (listId: string, values: string[]) =>
  localStorage.setItem(getStorageKey(CATEGORY_KEY, listId), JSON.stringify(values));

const persistMembers = (listId: string, values: string[]) =>
  localStorage.setItem(getStorageKey(MEMBER_KEY, listId), JSON.stringify(values));

const persistStatus = (listId: string, status: StatusFilter) =>
  localStorage.setItem(getStorageKey(STATUS_KEY, listId), status);

const WITHOUT_MEMBERS: NamedEntity = { id: WITHOUT_MEMBERS_ID, name: homeCopy.withoutMembers, rank: Infinity };

const getMembersInList = (members: NamedEntity[], items: PackItem[]) => {
  const memberIds = new Set(items.flatMap((i) => i.members.map((m) => m.id)));
  const filtered = members.filter((m) => memberIds.has(m.id)).sort((a, b) => b.rank - a.rank);
  if (filtered.length === 0) return [];
  const hasItemsWithoutMembers = items.some((item) => item.members.length === 0);
  return hasItemsWithoutMembers ? [WITHOUT_MEMBERS, ...filtered] : filtered;
};

export const useFilterDialog = (
  categories: NamedEntity[],
  members: NamedEntity[],
  items: PackItem[],
  listId?: string
): FilterDialogState => {
  const [visible, setVisible] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [, startTransition] = useTransition();
  const membersInList = getMembersInList(members, items);

  useEffect(() => {
    if (!listId) {
      setSelectedCategories([]);
      setSelectedMembers([]);
      setStatusFilter("all");
      return;
    }
    setSelectedCategories(parseStringArray(localStorage.getItem(getStorageKey(CATEGORY_KEY, listId))));
    setSelectedMembers(parseStringArray(localStorage.getItem(getStorageKey(MEMBER_KEY, listId))));
    setStatusFilter(parseStatus(localStorage.getItem(getStorageKey(STATUS_KEY, listId))));
  }, [listId]);

  const open = useCallback(() => setVisible(true), []);
  const close = useCallback(() => setVisible(false), []);

  const onToggleCategory = useCallback(
    (categoryId: string) => {
      startTransition(() => {
        setSelectedCategories((current) => {
          const updated = current.includes(categoryId)
            ? current.filter((id) => id !== categoryId)
            : [...current, categoryId];
          if (listId) void persistCategories(listId, updated);
          return updated;
        });
      });
    },
    [listId]
  );

  const onToggleMember = useCallback(
    (memberId: string) => {
      startTransition(() => {
        setSelectedMembers((current) => {
          const updated = current.includes(memberId) ? current.filter((id) => id !== memberId) : [...current, memberId];
          if (listId) void persistMembers(listId, updated);
          return updated;
        });
      });
    },
    [listId]
  );

  const onSetStatus = useCallback(
    (status: StatusFilter) => {
      startTransition(() => {
        setStatusFilter(status);
        if (listId) void persistStatus(listId, status);
      });
    },
    [listId]
  );

  const onClear = useCallback(() => {
    startTransition(() => {
      setSelectedCategories([]);
      setSelectedMembers([]);
      setStatusFilter("all");
      if (!listId) return;
      void persistCategories(listId, []);
      void persistMembers(listId, []);
      void persistStatus(listId, "all");
    });
  }, [listId]);

  const hasActiveFilter = selectedCategories.length > 0 || selectedMembers.length > 0 || statusFilter !== "all";
  return {
    visible,
    categories,
    selectedCategories,
    members: membersInList,
    selectedMembers,
    statusFilter,
    hasActiveFilter,
    open,
    close,
    onToggleCategory,
    onToggleMember,
    onSetStatus,
    onClear,
  };
};
